import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "./ExperienceMediaSlider.css";

/**
 * Props:
 * - mainMedia: { id, url, type: "video" | "image", visibility?: "public" | "private" }
 * - additionalMedia: Array<{ id, url, type, visibility }>
 * - isLoggedIn: boolean
 * - onLoginPrompt: () => void (invoked when the locked slide/button is clicked)
 */
const ExperienceMediaSlider = ({
  mainMedia,
  additionalMedia = [],
  isLoggedIn,
  onLoginPrompt,
  clientConfig = {},
  shouldAutoplayPrimary = true,
}) => {
  const sliderRef = useRef(null);
  const videoRefs = useRef({});
  const [containerWidth, setContainerWidth] = useState(0);
  const [slideRatios, setSlideRatios] = useState({});
  const [soundEnabledSlides, setSoundEnabledSlides] = useState({});
  const [galleryIndex, setGalleryIndex] = useState(null);
  const touchStartXRef = useRef(null);
  const touchStartYRef = useRef(null);
  const galleryTouchStartXRef = useRef(null);
  const galleryTouchStartYRef = useRef(null);

  useEffect(() => {
    const updateWidth = () => {
      if (sliderRef.current) {
        setContainerWidth(sliderRef.current.clientWidth);
      }
    };
    updateWidth();
    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, []);

  const aspectRatio = clientConfig.aspectRatio || 16 / 9;
  const maxHeight = clientConfig.maxHeight || 520;
  const minHeight = clientConfig.minHeight || 280;
  const slides = useMemo(() => {
    const normalizedAdditional = additionalMedia
      .map((media) => ({
        ...media,
        type: media.type || media.media_type || "image",
        visibility: media.visibility || "public",
      }))
      .filter((media) => media.url);
    
    const visibleAdditional = isLoggedIn
      ? normalizedAdditional
      : normalizedAdditional.filter((item) => item.visibility !== "private"); // still hide private data for guests

    const mainKey = mainMedia?.id || mainMedia?.url;
    const filteredAdditional = visibleAdditional.filter((media) => {
      return !mainKey || (media.id || media.url) !== mainKey;
    });

    const baseSlides = [];
    if (mainMedia && mainMedia.url) {
      baseSlides.push({
        ...mainMedia,
        type: mainMedia.type || "video",
        visibility: mainMedia.visibility || "public",
        slideId: `hero-${mainKey || "main"}`,
        isPrimary: true,
      });
    }

    filteredAdditional.forEach((item, index) => {
      baseSlides.push({
        ...item,
        slideId: `${item.id || item.url}-${index}`,
      });
    });

    if (!isLoggedIn) {
      baseSlides.push({
        slideId: "locked-private-0",
        type: "locked",
      });
    }

    return baseSlides.length > 0
      ? baseSlides
      : [
          {
            slideId: "empty",
            type: "placeholder",
          },
        ];
  }, [mainMedia, additionalMedia, isLoggedIn]);

  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    setCurrentIndex(0);
  }, [slides.length]);

  useEffect(() => {
    const primarySlide = slides.find((slide) => slide.isPrimary && slide.type === "video");
    if (!primarySlide) return;

    const video = videoRefs.current[primarySlide.slideId];
    if (!video) return;

    if (!shouldAutoplayPrimary) {
      video.pause();
      video.currentTime = 0;
      return;
    }

    video.play().catch(() => {
      // Browsers may still block playback. Native controls remain available.
    });
  }, [shouldAutoplayPrimary, slides]);

  const totalSlides = slides.length;
  const currentSlide = slides[currentIndex];
  const gallerySlides = useMemo(
    () => slides.filter((slide) => ["image", "video"].includes(slide.type) && slide.url),
    [slides]
  );
  const activeGallerySlide =
    galleryIndex !== null && gallerySlides.length > 0 ? gallerySlides[galleryIndex] : null;
  const currentRatio = slideRatios[currentSlide?.slideId] || aspectRatio;
  const rawHeight =
    containerWidth > 0 && currentRatio ? containerWidth / currentRatio : maxHeight;
  const sliderHeight = Math.max(minHeight, Math.min(rawHeight, maxHeight));

  const goNext = () => {
    setCurrentIndex((prev) => (prev + 1) % totalSlides);
  };

  const goPrev = () => {
    setCurrentIndex((prev) => (prev - 1 + totalSlides) % totalSlides);
  };

  const openGallery = (slide) => {
    const nextIndex = gallerySlides.findIndex((item) => item.slideId === slide.slideId);
    if (nextIndex >= 0) {
      setGalleryIndex(nextIndex);
    }
  };

  const closeGallery = () => {
    setGalleryIndex(null);
    galleryTouchStartXRef.current = null;
    galleryTouchStartYRef.current = null;
  };

  const goNextGallery = () => {
    if (gallerySlides.length <= 1) return;
    setGalleryIndex((prev) => ((prev ?? 0) + 1) % gallerySlides.length);
  };

  const goPrevGallery = () => {
    if (gallerySlides.length <= 1) return;
    setGalleryIndex((prev) => ((prev ?? 0) - 1 + gallerySlides.length) % gallerySlides.length);
  };

  const handleTouchStart = (event) => {
    const touch = event.touches?.[0];
    if (!touch) return;
    touchStartXRef.current = touch.clientX;
    touchStartYRef.current = touch.clientY;
  };

  const handleTouchEnd = (event) => {
    if (touchStartXRef.current === null || touchStartYRef.current === null) return;

    const touch = event.changedTouches?.[0];
    if (!touch) return;

    const deltaX = touch.clientX - touchStartXRef.current;
    const deltaY = touch.clientY - touchStartYRef.current;
    touchStartXRef.current = null;
    touchStartYRef.current = null;

    if (Math.abs(deltaX) < 48 || Math.abs(deltaX) < Math.abs(deltaY) * 1.25) return;

    if (deltaX < 0) {
      goNext();
    } else {
      goPrev();
    }
  };

  const handleGalleryTouchStart = (event) => {
    const touch = event.touches?.[0];
    if (!touch) return;
    galleryTouchStartXRef.current = touch.clientX;
    galleryTouchStartYRef.current = touch.clientY;
  };

  const handleGalleryTouchEnd = (event) => {
    if (galleryTouchStartXRef.current === null || galleryTouchStartYRef.current === null) return;

    const touch = event.changedTouches?.[0];
    if (!touch) return;

    const deltaX = touch.clientX - galleryTouchStartXRef.current;
    const deltaY = touch.clientY - galleryTouchStartYRef.current;
    galleryTouchStartXRef.current = null;
    galleryTouchStartYRef.current = null;

    if (Math.abs(deltaX) < 44 || Math.abs(deltaX) < Math.abs(deltaY) * 1.2) return;

    if (deltaX < 0) {
      goNextGallery();
    } else {
      goPrevGallery();
    }
  };

  const saveSlideRatio = (slideId, width, height) => {
    if (!slideId || !width || !height) return;
    const ratio = width / height;
    setSlideRatios((prev) => {
      if (prev[slideId] === ratio) return prev;
      return { ...prev, [slideId]: ratio };
    });
  };

  const handleImageLoad = (event, slide) => {
    saveSlideRatio(slide.slideId, event.target.naturalWidth, event.target.naturalHeight);
  };

  const handleVideoMetadata = (event, slide) => {
    saveSlideRatio(slide.slideId, event.target.videoWidth, event.target.videoHeight);
  };

  const enableVideoSound = async (slide) => {
    const video = videoRefs.current[slide.slideId];
    if (!video) return;

    video.muted = false;
    video.volume = 1;
    setSoundEnabledSlides((current) => ({ ...current, [slide.slideId]: true }));

    try {
      await video.play();
    } catch (_error) {
      // If the browser still blocks playback, the native controls remain available.
    }
  };

  const renderMediaItem = (slide) => {
    if (!slide || !slide.url) return null;

    if (slide.type === "image") {
      return (
        <button
          type="button"
          className={`slide-media-shell is-clickable-image ${slide.isPrimary ? "is-primary" : ""}`}
          aria-label="Open image"
          onClick={() => openGallery(slide)}
        >
          <img
            src={slide.url}
            alt={slide.title || "Event media"}
            className="slide-media"
            loading="lazy"
            onLoad={(event) => handleImageLoad(event, slide)}
          />
        </button>
      );
    }

    return (
      <div className={`slide-media-shell ${slide.isPrimary ? "is-primary" : ""}`}>
        <video
          ref={(node) => {
            if (node) {
              videoRefs.current[slide.slideId] = node;
            } else {
              delete videoRefs.current[slide.slideId];
            }
          }}
          className="slide-media"
          src={slide.url}
          controls
          preload="metadata"
          poster={slide.poster}
          autoPlay={slide.isPrimary && shouldAutoplayPrimary}
          muted={slide.isPrimary && !soundEnabledSlides[slide.slideId]}
          loop={slide.isPrimary}
          playsInline
          onLoadedMetadata={(event) => handleVideoMetadata(event, slide)}
          onVolumeChange={(event) => {
            if (!event.currentTarget.muted) {
              setSoundEnabledSlides((current) => ({ ...current, [slide.slideId]: true }));
            }
          }}
        >
          Your browser does not support the video tag.
        </video>
        {slide.isPrimary && !soundEnabledSlides[slide.slideId] ? (
          <button
            type="button"
            className="sound-toggle-button"
            onClick={() => enableVideoSound(slide)}
          >
            Tap for sound
          </button>
        ) : null}
      </div>
    );
  };

  const galleryOverlay = activeGallerySlide ? (
    <div
      className="image-gallery-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Image gallery"
      onTouchStart={handleGalleryTouchStart}
      onTouchEnd={handleGalleryTouchEnd}
    >
      <button
        type="button"
        className="image-gallery-close"
        onClick={closeGallery}
        aria-label="Close image gallery"
      >
        ×
      </button>

      {gallerySlides.length > 1 && (
        <button
          type="button"
          className="image-gallery-arrow prev"
          onClick={goPrevGallery}
          aria-label="Previous image"
        >
          ‹
        </button>
      )}

      {activeGallerySlide.type === "video" ? (
        <video
          src={activeGallerySlide.url}
          className="image-gallery-media"
          controls
          playsInline
          preload="metadata"
          poster={activeGallerySlide.poster}
        >
          Your browser does not support the video tag.
        </video>
      ) : (
        <img
          src={activeGallerySlide.url}
          alt={activeGallerySlide.title || "Event media"}
          className="image-gallery-media"
        />
      )}

      {gallerySlides.length > 1 && (
        <button
          type="button"
          className="image-gallery-arrow next"
          onClick={goNextGallery}
          aria-label="Next image"
        >
          ›
        </button>
      )}

      <div className="image-gallery-counter">
        {galleryIndex + 1} / {gallerySlides.length}
      </div>
    </div>
  ) : null;

  return (
    <div
      className="media-experience-slider"
      ref={sliderRef}
      style={{ height: sliderHeight, minHeight: sliderHeight }}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      <div
        className="slider-track"
        style={{ transform: `translateX(-${currentIndex * 100}%)`, minHeight: sliderHeight }}
      >
        {slides.map((slide) => (
          <div className="experience-slide" key={slide.slideId}>
            {slide.type === "locked" ? (
              <div className="locked-slide">
                <p>Login to view private memories</p>
                <button type="button" onClick={onLoginPrompt} className="primary-btn">
                  Unlock with Login
                </button>
              </div>
            ) : slide.type === "placeholder" ? (
              <div className="locked-slide">
                <p>Add a hero video to start sharing memories.</p>
              </div>
            ) : (
              renderMediaItem(slide)
            )}
          </div>
        ))}
      </div>

      {totalSlides > 1 && (
        <>
          <div className="slider-media-badge" aria-label={`${totalSlides} media items`}>
            <span className="slider-media-badge-icon" aria-hidden="true" />
            <span>{totalSlides}</span>
          </div>
          <div className="slider-edge-cue" aria-hidden="true">
            <span />
          </div>
          <button className="nav-arrow prev" onClick={goPrev} aria-label="Previous slide">
            ‹
          </button>
          <button className="nav-arrow next" onClick={goNext} aria-label="Next slide">
            ›
          </button>
        </>
      )}

      <div className="slider-dots">
        {slides.map((_, index) => (
          <button
            key={index}
            className={`dot ${currentIndex === index ? "active" : ""}`}
            onClick={() => setCurrentIndex(index)}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>

      {galleryOverlay && createPortal(galleryOverlay, document.body)}
      {false && activeGallerySlide && (
        <div
          className="image-gallery-overlay"
          role="dialog"
          aria-modal="true"
          aria-label="Image gallery"
          onTouchStart={handleGalleryTouchStart}
          onTouchEnd={handleGalleryTouchEnd}
        >
          <button
            type="button"
            className="image-gallery-close"
            onClick={closeGallery}
            aria-label="Close image gallery"
          >
            ×
          </button>

          {gallerySlides.length > 1 && (
            <button
              type="button"
              className="image-gallery-arrow prev"
              onClick={goPrevGallery}
              aria-label="Previous image"
            >
              ‹
            </button>
          )}

          {activeGallerySlide.type === "video" ? (
            <video
              src={activeGallerySlide.url}
              className="image-gallery-media"
              controls
              playsInline
              preload="metadata"
              poster={activeGallerySlide.poster}
            >
              Your browser does not support the video tag.
            </video>
          ) : (
            <img
              src={activeGallerySlide.url}
              alt={activeGallerySlide.title || "Event media"}
              className="image-gallery-media"
            />
          )}

          {gallerySlides.length > 1 && (
            <button
              type="button"
              className="image-gallery-arrow next"
              onClick={goNextGallery}
              aria-label="Next image"
            >
              ›
            </button>
          )}

          <div className="image-gallery-counter">
            {galleryIndex + 1} / {gallerySlides.length}
          </div>
        </div>
      )}
    </div>
  );
};

export default ExperienceMediaSlider;
